// ─────────────────────────────────────────────────────────────────────────────
// Aequum Capital — Client Portal
// Single-file app: brand tokens + API helper + LoginPage + Home (sidebar + tabs).
// Auth: POST /api/login -> { user, token }. Token (JWT, carries accountId) is
// sent on every call via the `x-cp-token` header (Azure SWA rewrites the
// standard Authorization header, so we use a custom one). The API derives
// accountId from the token, so a client only ever sees their own account.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { MdHome, MdAccountBalance, MdAccountBalanceWallet, MdCheckCircle, MdErrorOutline, MdDescription, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { FaFileExcel } from "react-icons/fa";
import { usePlaidLink } from "react-plaid-link";
import logo from "./logo.png";
import mark from "./mark.png";
import wordmark from "./wordmark.png";
import { t } from "./terms";

// ─── Copyright notice ─────────────────────────────────────────
// Shown at the foot of the sign-in screen and of every page once signed in.
// The holder is the legal entity that owns this software; the year is the
// current one, so the notice never goes stale. Change the name here only.
const COPYRIGHT_HOLDER = "Aequum Capital Financial, LLC";
const copyrightText = () => `© ${new Date().getFullYear()} ${COPYRIGHT_HOLDER}. All rights reserved.`;

// ── Brand tokens ──
// Each value reads a CSS variable (defaults in index.css :root) with the original color as
// a fallback, so a tenant can re-skin the whole portal at runtime via applyTenantTheme()
// (see tenantTheme.js). With no override these render exactly as before; keys are unchanged.
const B = {
  cyan:     "var(--brand-cyan, #12b5db)",
  cyanDark: "var(--brand-cyan-dark, #116588)",
  cyanLight:"var(--brand-cyan-light, #d4f1f9)",
  navyDeep: "var(--brand-navy-deep, #0a1a24)",
};

// Palette — nav bar + top menu bar use the portfolio's #0d3d52 teal chrome;
// the main content is white like the portfolio app. Cyan (#12b5db) is the accent.
const D = {
  bg:            "var(--brand-bg, #ffffff)",                        // main page — white, like the portfolio
  sidebar:       "var(--brand-sidebar, #0d3d52)",                   // portfolio nav bar color
  sidebarHover:  "var(--brand-sidebar-hover, rgba(18,181,219,0.15))",
  sidebarActive: "var(--brand-sidebar-active, #12b5db)",
  navText:       "var(--brand-nav-text, rgba(255,255,255,0.78))",
  navLabel:      "var(--brand-nav-label, rgba(255,255,255,0.45))",
  navBorder:     "var(--brand-nav-border, rgba(255,255,255,0.10))",
  card:          "var(--brand-card, #ffffff)",
  cardBorder:    "var(--brand-card-border, #e5e9ed)",
  headerBar:     "var(--brand-header-bar, #0d3d52)",               // portfolio top menu bar color
  tableHead:     "var(--brand-table-head, #f5f7f9)",
  text:          "var(--brand-text, #15242d)",
  textDim:       "var(--brand-text-dim, #5f6d77)",
  label:         "var(--brand-label, #7a8791)",
  rowHover:      "var(--brand-row-hover, #f5f7f9)",
  tabBorder:     "var(--brand-tab-border, #e5e9ed)",
};

const TOKEN_KEY = "cp_token";
const USER_KEY = "cp_user";

// Cloudflare Turnstile public site key. Set REACT_APP_TURNSTILE_SITEKEY at build time
// (or paste the key here). Leave empty to disable the widget entirely.
const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || "0x4AAAAAAENJAD_qfubmZ0u9";
// Auto sign-out after this much inactivity.
const IDLE_LIMIT_MS = 4 * 60 * 60 * 1000; // 4 hours

// ── Formatting helpers ──
const fmt = (n) =>
  n === null || n === undefined || n === ""
    ? "$ 0.00"
    : "$ " + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = String(d).slice(0, 10).split("-");
  if (!y || !m || !day) return String(d);
  return `${m}/${day}/${y.slice(2)}`;
};

const today = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}`;
};

// CSV export (opens in Excel). ISO dates → M/D/YYYY; numbers left unquoted so
// Excel reads them as numbers; text with special chars is quoted/escaped.
function exportCSV(rows, cols, filename) {
  const cell = (v) => {
    if (v === null || v === undefined) return "";
    let s = String(v);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) s = `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const header = cols.map((c) => cell(c.label)).join(",");
  const body = rows.map((r) => cols.map((c) => cell(r[c.key])).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── API helper (token via x-cp-token) ──
const api = {
  async get(path, params) {
    const token = localStorage.getItem(TOKEN_KEY);
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    const res = await fetch("/api/" + path + qs, { headers: token ? { "x-cp-token": token } : {} });
    if (res.status === 401) throw new Error("UNAUTHORIZED");
    if (!res.ok) throw new Error(await errText(res, "GET " + path));
    return res.json();
  },
  async post(path, body) {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { "Content-Type": "application/json" };
    if (token) headers["x-cp-token"] = token;
    const res = await fetch("/api/" + path, { method: "POST", headers, body: JSON.stringify(body) });
    if (res.status === 401) throw new Error("UNAUTHORIZED");
    if (!res.ok) throw new Error(await errText(res, "POST " + path));
    return res.json();
  },
};
async function errText(res, prefix) {
  try {
    const b = await res.json();
    if (b && b.error) return b.error;
  } catch (_) {}
  return prefix + " failed: " + res.status;
}

// ── Trusted browsers ──
// "Trust this browser for 30 days" leaves a random token here, per email, so
// two people sharing a computer each keep their own. The server stores only a
// hash of it; a stale or revoked token simply means the code is asked for again.
const TRUST_KEY = "cp_trusted_devices";
const readTrusted = () => { try { return JSON.parse(localStorage.getItem(TRUST_KEY) || "{}") || {}; } catch (_) { return {}; } };
const deviceTokenFor = (em) => readTrusted()[String(em || "").toLowerCase()] || "";
const saveDeviceToken = (em, tok) => {
  try { const m = readTrusted(); if (tok) m[String(em).toLowerCase()] = tok; else delete m[String(em).toLowerCase()];
        localStorage.setItem(TRUST_KEY, JSON.stringify(m)); } catch (_) {}
};

// ─── LOGIN PAGE (email + password, then an emailed code; SWA-safe) ───
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Second step: the six-digit code emailed after a correct password.
  const [challenge, setChallenge] = useState(null);   // { challengeId, sentTo }
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [info, setInfo] = useState(null);
  const [tsToken, setTsToken] = useState("");   // Turnstile token for the next submit
  const tsRef = useRef(null);
  const tsWidgetId = useRef(null);

  // Load Cloudflare Turnstile and render the widget once (only when a site key is configured).
  useEffect(() => {
    if (!TURNSTILE_SITEKEY) return;
    const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    const renderWidget = () => {
      if (!window.turnstile || !tsRef.current || tsWidgetId.current !== null) return;
      tsWidgetId.current = window.turnstile.render(tsRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        theme: "dark",
        callback: (t) => setTsToken(t),
        "error-callback": () => setTsToken(""),
        "expired-callback": () => setTsToken(""),
      });
    };
    if (window.turnstile) { renderWidget(); }
    else {
      let s = document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile"]');
      if (!s) { s = document.createElement("script"); s.src = SRC; s.async = true; s.defer = true; s.onload = renderWidget; document.head.appendChild(s); }
      else { s.addEventListener("load", renderWidget); }
    }
    return () => { try { if (window.turnstile && tsWidgetId.current !== null) window.turnstile.remove(tsWidgetId.current); } catch (e) {} tsWidgetId.current = null; };
  }, []);

  const resetTurnstile = () => { try { if (window.turnstile && tsWidgetId.current !== null) window.turnstile.reset(tsWidgetId.current); } catch (e) {} setTsToken(""); };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITEKEY && !tsToken) { setError("Please complete the verification below."); return; }
    setLoading(true);
    setError(null);
    try {
      const em = email.trim().toLowerCase();
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em, password, turnstileToken: tsToken, deviceToken: deviceTokenFor(em) }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Invalid credentials");
        setLoading(false);
        resetTurnstile();
        return;
      }
      if (data.mfaRequired) {
        // A saved token that did not skip the code is stale or revoked — forget it.
        saveDeviceToken(em, null);
        setChallenge({ challengeId: data.challengeId, sentTo: data.sentTo });
        setCode(""); setInfo(null); setPassword("");
        setLoading(false);
        return;
      }
      finish(em, data);
    } catch (err) {
      setError("Login failed. Please try again.");
      setLoading(false);
      resetTurnstile();
    }
  };

  const finish = (em, data) => {
    if (data.deviceToken) saveDeviceToken(em, data.deviceToken);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    onLogin(data.user);
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setInfo(null);
    try {
      const res = await fetch("/api/loginVerify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: challenge.challengeId, code, trustDevice: trust }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "That code did not work."); setLoading(false); return; }
      finish(email.trim().toLowerCase(), data);
    } catch (err) { setError("Sign-in failed. Please try again."); setLoading(false); }
  };

  const resend = async () => {
    setError(null); setInfo(null);
    try {
      const res = await fetch("/api/loginVerify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId: challenge.challengeId, action: "resend" }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || "Could not send a new code."); else setInfo("A new code is on its way to " + data.sentTo + ".");
    } catch (err) { setError("Could not send a new code."); }
  };

  const backToPassword = () => { setChallenge(null); setCode(""); setError(null); setInfo(null); resetTurnstile(); };

  const linkBtn = { background: "none", border: "none", color: B.cyan, fontSize: 12, cursor: "pointer", padding: 0 };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-sans)", position: "relative", overflow: "hidden", background: B.navyDeep }}>
      <picture>
        <source type="image/avif" sizes="100vw" srcSet="/background-1280.avif 1280w, /background-1920.avif 1920w, /background-2560.avif 2560w, /background-3840.avif 3840w" />
        <source type="image/webp" sizes="100vw" srcSet="/background-1280.webp 1280w, /background-1920.webp 1920w, /background-2560.webp 2560w, /background-3840.webp 3840w" />
        <img src="/background-1920.webp" alt="" aria-hidden="true" decoding="async" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.18 }} />
      </picture>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(18,181,219,0.04) 0%, rgba(10,26,36,0.7) 100%)" }} />
      <div style={{ position: "relative", zIndex: 10, width: 360, maxWidth: "92vw", background: "rgba(13,28,38,0.92)", border: "1px solid " + B.cyan, borderRadius: 12, padding: "53px 32px", boxShadow: "0 0 40px rgba(18,181,219,0.18), 0 0 0 1px rgba(18,181,219,0.08)" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <img src={logo} alt="Aequum Capital" style={{ height: 40, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
        </div>
        {challenge ? (
        <form onSubmit={verify}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginBottom: 28, lineHeight: 1.5, textAlign: "center" }}>
            We emailed a 6-digit sign-in code to <strong style={{ color: "#fff" }}>{challenge.sentTo}</strong>. It expires in 10 minutes.
          </div>
          <div style={{ marginBottom: 28 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.85)", marginBottom: 6 }}>Sign-in code</label>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456"
              inputMode="numeric" autoComplete="one-time-code" autoFocus
              style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none", borderBottom: "1px solid rgba(18,181,219,0.4)", padding: "8px 0", fontSize: 22, letterSpacing: "0.4em", color: "#fff", outline: "none", caretColor: B.cyan, textAlign: "center" }} />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: "rgba(255,255,255,0.75)", marginBottom: 24, cursor: "pointer" }}>
            <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Trust this browser for 30 days <span style={{ color: "rgba(255,255,255,0.5)" }}>(don't use on a shared computer)</span></span>
          </label>
          {error && <div style={{ background: "rgba(226,75,74,0.15)", border: "0.5px solid rgba(226,75,74,0.4)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#f09595", marginBottom: 16 }}>{error}</div>}
          {info && <div style={{ background: "rgba(18,181,219,0.12)", border: "0.5px solid rgba(18,181,219,0.4)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#bfeaf6", marginBottom: 16 }}>{info}</div>}
          <button type="submit" disabled={loading || code.length !== 6} style={{ width: "100%", padding: "12px 16px", background: B.cyan, border: "none", borderRadius: 6, color: "#fff", fontSize: 14, fontWeight: 500, cursor: (loading || code.length !== 6) ? "not-allowed" : "pointer", opacity: (loading || code.length !== 6) ? 0.7 : 1, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {loading ? "Verifying..." : "Verify"}
          </button>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
            <button type="button" onClick={backToPassword} style={linkBtn}>← Back</button>
            <button type="button" onClick={resend} style={linkBtn}>Send a new code</button>
          </div>
        </form>
        ) : (
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 40 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.85)", marginBottom: 26 }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" autoComplete="username"
              style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none", borderBottom: "1px solid rgba(18,181,219,0.4)", padding: "8px 0", fontSize: 13, color: "#fff", outline: "none", caretColor: B.cyan }} />
          </div>
          <div style={{ marginBottom: 48 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.85)", marginBottom: 6 }}>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password"
              style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none", borderBottom: "1px solid rgba(18,181,219,0.4)", padding: "8px 0", fontSize: 13, color: "#fff", outline: "none", caretColor: B.cyan }} />
          </div>
          {TURNSTILE_SITEKEY && <div ref={tsRef} style={{ marginBottom: 24, display: "flex", justifyContent: "center" }} />}
          {error && <div style={{ background: "rgba(226,75,74,0.15)", border: "0.5px solid rgba(226,75,74,0.4)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#f09595", marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading || (TURNSTILE_SITEKEY && !tsToken)} style={{ width: "100%", padding: "12px 16px", background: B.cyan, border: "none", borderRadius: 6, color: "#fff", fontSize: 14, fontWeight: 500, cursor: (loading || (TURNSTILE_SITEKEY && !tsToken)) ? "not-allowed" : "pointer", opacity: (loading || (TURNSTILE_SITEKEY && !tsToken)) ? 0.7 : 1, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {loading ? "Signing in..." : "Login"}
          </button>
        </form>
        )}
      </div>
      <div style={{ position: "absolute", bottom: 16, left: 0, right: 0, zIndex: 10, textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.45)", letterSpacing: "0.02em" }}>
        {copyrightText()}
      </div>
    </div>
  );
}

// ─── Shared UI bits ───────────────────────────────────────────
// LoanPage-style card title: small uppercase cyan-dark text with a light-cyan
// underline that runs across the card's content width (inset by the padding).
const CardTitle = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14, paddingBottom: 6, borderBottom: "1px solid " + B.cyanLight }}>{children}</div>
);

const Card = ({ title, children, style }) => (
  <div style={{ background: D.card, border: "1px solid " + D.cardBorder, borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", padding: 16, ...style }}>
    {title && <CardTitle>{title}</CardTitle>}
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 4 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: D.label, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 15, color: D.text }}>{value === null || value === undefined || value === "" ? "—" : value}</div>
  </div>
);

const FieldGrid = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 24px" }}>{children}</div>
);

// Paginated, sortable data table. Click any column header to sort; click again
// to flip direction. Numeric/currency columns sort numerically, others as text
// (ISO date strings sort chronologically).
function DataTable({ columns, rows, pageSize = 20, groupBy, filterKey, exportName }) {
  const [page, setPage] = useState(1);
  const [sortIdx, setSortIdx] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [filterVal, setFilterVal] = useState("");
  useEffect(() => { setPage(1); setFilterVal(""); }, [rows]);
  if (!rows || rows.length === 0) return <div style={{ fontSize: 13, color: D.textDim, padding: "16px 4px" }}>No records.</div>;

  // Distinct filter values (e.g., loan numbers) for the dropdown.
  const filterOptions = filterKey
    ? Array.from(new Set(rows.map((r) => r[filterKey]).filter((v) => v !== null && v !== undefined && v !== "")))
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
    : [];

  // 1) Filter
  const base = filterKey && filterVal ? rows.filter((r) => String(r[filterKey]) === String(filterVal)) : rows;

  // 2) Sort
  let data = base;
  if (sortIdx !== null && columns[sortIdx]) {
    const col = columns[sortIdx];
    const numeric = (col.sort || (col.align === "right" ? "num" : "text")) === "num";
    data = [...base].sort((a, b) => {
      let av = a[col.key], bv = b[col.key];
      if (numeric) {
        av = parseFloat(av); bv = parseFloat(bv);
        if (isNaN(av)) av = -Infinity; if (isNaN(bv)) bv = -Infinity;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      av = (av === null || av === undefined ? "" : String(av)).toLowerCase();
      bv = (bv === null || bv === undefined ? "" : String(bv)).toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }

  // Group rows (stable) so each group's rows stay contiguous, then splice in
  // group-header marker rows. Sorting within a group is preserved.
  let items = data;
  if (groupBy) {
    const g = (v) => (v === null || v === undefined ? "" : String(v));
    const sorted = (data === base ? [...base] : data).sort((a, b) =>
      g(a[groupBy]).localeCompare(g(b[groupBy]), undefined, { numeric: true }));
    items = [];
    let cur;
    for (const r of sorted) {
      const gv = r[groupBy];
      if (gv !== cur) { items.push({ __isGroup: true, name: gv }); cur = gv; }
      items.push(r);
    }
  }

  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const p = Math.min(page, pages);
  const slice = items.slice((p - 1) * pageSize, p * pageSize);

  const toggleSort = (ci) => {
    if (sortIdx === ci) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortIdx(ci); setSortDir("asc"); }
    setPage(1);
  };

  const doExport = () => {
    const expCols = [];
    if (groupBy) expCols.push({ key: groupBy, label: groupBy === "loannumber" ? "Loan Number" : groupBy });
    columns.forEach((c) => expCols.push({ key: c.key, label: c.label }));
    exportCSV(data, expCols, (exportName || "export") + (filterVal ? "-" + filterVal : "") + ".csv");
  };

  const showToolbar = (filterKey && filterOptions.length > 1) || exportName;

  return (
    <div>
      {showToolbar && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div>
            {filterKey && filterOptions.length > 1 && (
              <select value={filterVal} onChange={(e) => { setFilterVal(e.target.value); setPage(1); }}
                style={{ padding: "6px 10px", fontSize: 13, borderRadius: 6, border: "1px solid " + D.cardBorder, background: "#fff", color: D.text }}>
                <option value="">All loans</option>
                {filterOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>
          {exportName && (
            <button onClick={doExport} title="Export to Excel"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "1px solid " + D.cardBorder, background: "#fff", color: "#1d6f42", cursor: "pointer", fontWeight: 500 }}>
              <FaFileExcel size={16} color="#1d6f42" /> Export
            </button>
          )}
        </div>
      )}
      {data.length === 0 ? (
        <div style={{ fontSize: 13, color: D.textDim, padding: "16px 4px" }}>No records for this filter.</div>
      ) : (
      <>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid " + D.cardBorder }}>
              {columns.map((c, ci) => (
                <th key={ci} onClick={() => toggleSort(ci)}
                  style={{ textAlign: c.align || "left", padding: "10px 14px", color: sortIdx === ci ? B.cyan : D.label, fontWeight: 600, fontSize: 12, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: D.tableHead }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexDirection: c.align === "right" ? "row-reverse" : "row" }}>
                    {c.label}
                    <span style={{ fontSize: 9, opacity: sortIdx === ci ? 1 : 0.3 }}>{sortIdx === ci ? (sortDir === "asc" ? "▲" : "▼") : "▲"}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.map((r, i) =>
              r.__isGroup ? (
                <tr key={"g" + i}>
                  <td colSpan={columns.length} style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: B.cyanDark, background: B.cyanLight }}>
                    {groupBy === "loannumber" ? "Loan Number: " : ""}{r.name === null || r.name === undefined || r.name === "" ? "—" : r.name}
                  </td>
                </tr>
              ) : (
                <tr key={i} style={{ borderBottom: "1px solid " + D.cardBorder }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = D.rowHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                  {columns.map((c, ci) => (
                    <td key={ci} style={{ textAlign: c.align || "left", padding: "10px 14px", color: D.text, fontVariantNumeric: c.align === "right" ? "tabular-nums" : "normal" }}>
                      {c.render ? c.render(r[c.key], r) : (r[c.key] === null || r[c.key] === undefined || r[c.key] === "" ? "—" : r[c.key])}
                    </td>
                  ))}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, fontSize: 12, color: D.textDim }}>
        <span>Page {p} of {pages}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <PgBtn disabled={p <= 1} onClick={() => setPage(p - 1)}>Prev</PgBtn>
          <PgBtn disabled={p >= pages} onClick={() => setPage(p + 1)}>Next</PgBtn>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
const PgBtn = ({ disabled, onClick, children }) => (
  <button disabled={disabled} onClick={onClick}
    style={{ background: "none", border: "1px solid " + D.cardBorder, borderRadius: 6, color: disabled ? D.label : D.text, fontSize: 12, padding: "5px 12px", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
    {children}
  </button>
);

// ─── TABS ─────────────────────────────────────────────────────
const TABS = ["Account Overview", "Loans", "Ledger", "Received Payments", "Fundings", "Covenants"];

// ─── HOME ─────────────────────────────────────────────────────
function Home({ overview, loans }) {
  const [tab, setTab] = useState("Account Overview");
  const [cache, setCache] = useState({}); // tab -> { loading, error, rows }

  const loadTab = useCallback(async (name, path) => {
    setCache((c) => ({ ...c, [name]: { loading: true } }));
    try {
      const rows = await api.get(path);
      setCache((c) => ({ ...c, [name]: { loading: false, rows } }));
    } catch (e) {
      setCache((c) => ({ ...c, [name]: { loading: false, error: e.message } }));
    }
  }, []);

  useEffect(() => {
    if (tab === "Received Payments" && !cache["Received Payments"]) loadTab("Received Payments", "getMyReceivedPayments");
    if (tab === "Fundings" && !cache["Fundings"]) loadTab("Fundings", "getMyFundings");
    if (tab === "Ledger" && !cache["Ledger"]) loadTab("Ledger", "getMyLedger");
    if (tab === "Covenants" && !cache["Covenants"]) loadTab("Covenants", "getMyCovenants");
  }, [tab, cache, loadTab]);

  const acct = overview || {};
  const fundedLoanCnt = loans ? loans.length : "—";

  return (
    <div>
      {/* General card */}
      <Card title="General" style={{ marginBottom: 20 }}>
        <FieldGrid>
          <Field label="Company Name" value={acct.companyname} />
          <Field label="Data As Of Date" value={today()} />
          <Field label="Loan Balance" value={fmt(acct.bsloanbalanceeod)} />
          <Field label="Account Number" value={acct.accountnumber} />
        </FieldGrid>
      </Card>

      {/* Tabs — folder-style pills on a cyan baseline, mirroring the portfolio LoanPage */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 20, borderBottom: "4px solid " + B.cyan }}>
        {TABS.map((t) => {
          const active = tab === t;
          return (
            <button key={t} onClick={() => setTab(t)}
              style={{ background: active ? B.cyan : D.tableHead, border: "1px solid " + (active ? B.cyan : D.cardBorder), borderBottom: "none",
                borderRadius: "8px 8px 0 0", padding: "9px 22px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
                fontWeight: active ? 600 : 400, color: active ? "#fff" : D.textDim, position: "relative", zIndex: active ? 2 : 1,
                transition: "all 0.15s ease" }}>
              {t}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "Account Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 20 }}>
            <Card title="Balance Information">
              <FieldGrid>
                <Field label="Data As Of Date" value={today()} />
                <Field label="Funded Loan Cnt" value={fundedLoanCnt} />
                <Field label="Commitment Amount" value={fmt(acct.accountcommitmentamt)} />
                <Field label="Loan Balance" value={fmt(acct.bsloanbalanceeod)} />
              </FieldGrid>
            </Card>
            <Card title="Payment Summary">
              <FieldGrid>
                <Field label="Next Pmt Sched Date" value={fmtDate(acct.nextscheddate)} />
                <Field label="Next Scheduled Payment Amount" value={fmt(acct.nextschedamount)} />
              </FieldGrid>
            </Card>
          </div>
          <Card title="Contacts">
            <Field label="Portfolio Manager" value={acct.account_manager_email || acct.account_manager_name} />
          </Card>
        </div>
      )}

      {tab === "Loans" && (
        <Card>
          <DataTable
            rows={loans || []}
            filterKey="loannumber"
            exportName="loans"
            columns={[
              { key: "accountnumber", label: "Account Number" },
              { key: "loannumber", label: "Loan Number" },
              { key: "companyname", label: "Primary Borrower" },
              { key: "companyname", label: "Company Name" },
              { key: "facilitytype", label: "Facility Type" },
              { key: "commitmentamount", label: "Total Commitment Amount", align: "right", render: (v) => fmt(v) },
              { key: "bsloanbalanceeod", label: "Loan Balance", align: "right", render: (v) => fmt(v) },
            ]}
          />
        </Card>
      )}

      {tab === "Received Payments" && (
        <Card>
          <TabData state={cache["Received Payments"]} filterKey="loannumber" exportName="received-payments"
            columns={[
              { key: "loannumber", label: "Loan Number" },
              { key: "receiveddate", label: "Received Date", render: (v) => fmtDate(v) },
              { key: "receivedprincipal", label: "Received Principal", align: "right", render: (v) => fmt(v) },
              { key: "receivedinterest", label: "Received Interest", align: "right", render: (v) => fmt(v) },
              { key: "receivedfee", label: "Received Fee", align: "right", render: (v) => fmt(v) },
              { key: "receivedother", label: "Received Other", align: "right", render: (v) => fmt(v) },
              { key: "receivedpassthrough", label: "Pass Through Expenses", align: "right", render: (v) => fmt(v) },
              { key: "totalreceivedpmt", label: "Total Received Payment", align: "right", render: (v) => fmt(v) },
            ]}
          />
        </Card>
      )}

      {tab === "Fundings" && (
        <Card>
          <TabData state={cache["Fundings"]} filterKey="loannumber" exportName="fundings"
            columns={[
              { key: "loannumber", label: "Loan Number" },
              { key: "source", label: "Type" },
              { key: "fundeddate", label: "Funded Date", render: (v) => fmtDate(v) },
              { key: "fundingamount", label: "Funded Amt", align: "right", render: (v) => fmt(v) },
            ]}
          />
        </Card>
      )}

      {tab === "Ledger" && (
        <Card>
          <TabData state={cache["Ledger"]} groupBy="loannumber" filterKey="loannumber" exportName="ledger"
            columns={[
              { key: "date", label: "Date", render: (v) => fmtDate(v) },
              { key: "principalpaydown", label: t("principalPaydown", "Principal Paydown"), align: "right", render: (v) => fmt(v) },
              { key: "adjustments", label: t("adjustments", "Adjustments"), align: "right", render: (v) => fmt(v) },
              { key: "funding", label: "Funding", align: "right", render: (v) => fmt(v) },
              { key: "loanbalance", label: t("loanBalance", "Loan Balance"), align: "right", render: (v) => fmt(v) },
            ]}
          />
        </Card>
      )}

      {tab === "Covenants" && <CovenantsTab state={cache["Covenants"]} />}
    </div>
  );
}

// Renders a lazily-loaded tab's table with loading/error states.
function TabData({ state, columns, groupBy, filterKey, exportName }) {
  if (!state || state.loading) return <div style={{ fontSize: 13, color: D.textDim, padding: "16px 4px" }}>Loading…</div>;
  if (state.error) return <div style={{ fontSize: 13, color: "#f09595", padding: "16px 4px" }}>{state.error}</div>;
  return <DataTable rows={state.rows || []} columns={columns} groupBy={groupBy} filterKey={filterKey} exportName={exportName} />;
}

// Covenants: two sub-tabs (Financial vs Reporting Requirements) over the
// account's open / due-soon covenants. '3rd Party Exam' is already excluded by
// the API.
const COV_COLUMNS = [
  { key: "covenantname", label: "Covenant Name" },
  { key: "covenantrequirement", label: "Requirement" },
  { key: "covenantfrequency", label: "Frequency" },
  { key: "testdate", label: "Test Date", render: (v) => fmtDate(v) },
  { key: "duedate", label: "Due Date", render: (v) => fmtDate(v) },
  { key: "status", label: "Status" },
];

function CovenantsTab({ state }) {
  const [sub, setSub] = useState("Financial Covenants");
  if (!state || state.loading) return <Card><div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>Loading…</div></Card>;
  if (state.error) return <Card><div style={{ fontSize: 13, color: "#f09595", padding: "8px 4px" }}>{state.error}</div></Card>;

  const rows = state.rows || [];
  const SUBS = [
    { key: "Financial Covenants", rows: rows.filter((r) => (r.covenantcategory || "") === "Financial"), exportName: "financial-covenants" },
    { key: "Reporting Requirements", rows: rows.filter((r) => (r.covenantcategory || "") === "Reporting Requirements"), exportName: "reporting-requirements" },
  ];
  const active = SUBS.find((s) => s.key === sub) || SUBS[0];

  return (
    <>
    <div style={{ fontSize: 12, color: D.textDim, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: B.cyan, fontWeight: 700 }}>ⓘ</span>
      Showing covenants not yet completed and due within the next 30 days (past-due included).
    </div>
    <Card>
      <div style={{ display: "flex", gap: 24, borderBottom: "1px solid " + D.tabBorder, marginBottom: 16 }}>
        {SUBS.map((s) => {
          const a = sub === s.key;
          return (
            <button key={s.key} onClick={() => setSub(s.key)}
              style={{ background: "none", border: "none", padding: "8px 2px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
                fontWeight: a ? 600 : 400, color: a ? D.text : D.textDim, borderBottom: a ? "2px solid " + B.cyan : "2px solid transparent", marginBottom: -1 }}>
              {s.key} <span style={{ color: D.textDim, fontWeight: 400 }}>({s.rows.length})</span>
            </button>
          );
        })}
      </div>
      <DataTable rows={active.rows} columns={COV_COLUMNS} exportName={active.exportName} />
    </Card>
    </>
  );
}

// ─── ADD BANK (Plaid Link) ────────────────────────────────────
// Green "Active" / red "Not Active" pill for a linked item's health.
function StatusPill({ status }) {
  const active = status === "active";
  const c = active ? { bg: "#E1F5EE", fg: "#0F6E56", Icon: MdCheckCircle, label: "Active" }
                   : { bg: "#FAECE7", fg: "#993C1D", Icon: MdErrorOutline, label: "Not Active" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: c.bg, color: c.fg,
      borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
      <c.Icon size={14} /> {c.label}
    </span>
  );
}

// Repairs a broken item via Plaid Link update mode. The same access token is
// reused server-side; no public-token exchange is needed on success.
function RelinkButton({ item, onDone }) {
  const [token, setToken] = useState(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const onSuccess = useCallback(() => { setArmed(false); onDone && onDone(); }, [onDone]);
  const onExit = useCallback(() => { setArmed(false); }, []);
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  useEffect(() => { if (armed && ready && token) open(); }, [armed, ready, token, open]);

  const start = async () => {
    setBusy(true); setErr(null);
    try {
      const d = await api.post("createLinkToken", { relinkItemId: item.plaidItemId });
      setToken(d.link_token);
      setArmed(true);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <span>
      <button onClick={start} disabled={busy}
        style={{ background: B.cyan, color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px",
          fontSize: 12, fontWeight: 500, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Preparing…" : "Relink"}
      </button>
      {err && <span style={{ color: "#993C1D", fontSize: 12, marginLeft: 8 }}>{err}</span>}
    </span>
  );
}

function AddBank() {
  const [linkToken, setLinkToken] = useState(null);
  const [loadingToken, setLoadingToken] = useState(true);
  const [status, setStatus] = useState(null); // null | 'linking' | 'done'
  const [err, setErr] = useState(null);

  const [items, setItems] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsErr, setItemsErr] = useState(null);

  const fetchToken = useCallback(async () => {
    setLoadingToken(true);
    setErr(null);
    try {
      const d = await api.post("createLinkToken", {});
      setLinkToken(d.link_token);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoadingToken(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    setItemsErr(null);
    try { setItems(await api.get("getMyBankItems")); }
    catch (e) { setItemsErr(e.message); }
    finally { setItemsLoading(false); }
  }, []);

  useEffect(() => { fetchToken(); loadItems(); }, [fetchToken, loadItems]);

  const onSuccess = useCallback(async (public_token, metadata) => {
    setStatus("linking");
    setErr(null);
    try {
      await api.post("exchangePublicToken", {
        public_token,
        institution_name: metadata && metadata.institution && metadata.institution.name,
      });
      setStatus("done");
      loadItems();
    } catch (e) {
      setErr(e.message);
      setStatus(null);
    }
  }, [loadItems]);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess });

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: D.text, margin: "0 0 20px" }}>Add Bank</h1>
      <Card title="Connect a bank account">
        <p style={{ fontSize: 14, color: D.textDim, marginTop: 0, marginBottom: 16 }}>
          Securely connect your bank through Plaid. Aequum Capital never sees your banking login.
        </p>
        {err && <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>{err}</div>}
        {status === "done" ? (
          <div style={{ color: "#0F6E56", fontSize: 14, fontWeight: 500 }}>
            ✓ Bank linked. View it under <strong>Bank Accounts</strong>.
          </div>
        ) : (
          <button onClick={() => open()} disabled={!ready || loadingToken || status === "linking"}
            style={{ background: B.cyan, color: "#fff", border: "none", borderRadius: 6, padding: "11px 20px", fontSize: 14, fontWeight: 500,
              cursor: !ready || loadingToken || status === "linking" ? "not-allowed" : "pointer", opacity: !ready || loadingToken || status === "linking" ? 0.6 : 1 }}>
            {status === "linking" ? "Linking…" : loadingToken ? "Preparing…" : "Connect a bank"}
          </button>
        )}
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <CardTitle>Linked Banks</CardTitle>
        {itemsLoading ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>Loading…</div>
        ) : itemsErr ? (
          <div style={{ fontSize: 13, color: "#993C1D", padding: "8px 4px" }}>
            {itemsErr} <button onClick={loadItems} style={{ marginLeft: 8, background: "none", border: "none", color: B.cyanDark, textDecoration: "underline", cursor: "pointer" }}>Retry</button>
          </div>
        ) : !items || items.length === 0 ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>No linked banks yet.</div>
        ) : (
          <div>
            {items.map((it) => (
              <div key={it.plaidItemId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 4px", borderBottom: "1px solid " + D.cardBorder, gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: D.text }}>{it.institutionName || "Bank"}</div>
                  <div style={{ fontSize: 12, color: D.textDim, marginTop: 2 }}>
                    {(it.accounts || []).length
                      ? it.accounts.map((a) => (a.name || a.subtype || "Account") + (a.mask ? " ••" + a.mask : "")).join("  •  ")
                      : "No accounts"}
                    {it.status !== "active" && it.statusDetail ? "  —  " + it.statusDetail : ""}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                  <StatusPill status={it.status} />
                  {it.status !== "active" && <RelinkButton item={it} onDone={loadItems} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── BANK ACCOUNTS ────────────────────────────────────────────
function BankAccounts() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [txns, setTxns] = useState(null);
  const [txnLoading, setTxnLoading] = useState(true);
  const [txnErr, setTxnErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try { setRows(await api.get("getMyBankAccounts")); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    setTxnErr(null);
    try { setTxns(await api.get("getMyTransactions")); }
    catch (e) { setTxnErr(e.message); }
    finally { setTxnLoading(false); }
  }, []);

  useEffect(() => { load(); loadTxns(); }, [load, loadTxns]);

  // Give each transaction a single label to filter/group the whole list by one
  // bank account, and normalize the amount sign so credits read positive.
  const txnRows = (txns || []).map((t) => {
    const label = (t.bankAccountName || t.institution || "Account") + (t.bankMask ? " ••" + t.bankMask : "");
    return { ...t, bankAccountLabel: label, signedAmount: t.amount == null ? null : -t.amount };
  });

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: D.text, margin: "0 0 20px" }}>Bank Accounts</h1>
      <Card>
        <CardTitle>Accounts &amp; Balances</CardTitle>
        {loading ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>Loading…</div>
        ) : err ? (
          <div style={{ fontSize: 13, color: "#993C1D", padding: "8px 4px" }}>
            {err} <button onClick={load} style={{ marginLeft: 8, background: "none", border: "none", color: B.cyanDark, textDecoration: "underline", cursor: "pointer" }}>Retry</button>
          </div>
        ) : !rows || rows.length === 0 ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>No bank accounts on file yet. Use “Add Bank” to connect one.</div>
        ) : (
          <>
            {/* Two kinds of row, often side by side: accounts linked through
                Plaid (live balance) and accounts Aequum keeps from statements
                or files (latest balance on file, with its date). Needs-attention
                rows are a Plaid link that could not be read. */}
            <DataTable rows={rows} exportName="bank-accounts"
              columns={[
                { key: "institution", label: "Bank" },
                { key: "name", label: "Account", render: (v, r) => r.needsAttention ? <span style={{ color: "#993C1D" }}>Connection needs attention</span> : (v || "—") },
                { key: "mask", label: "Number", render: (v) => (v ? "••" + v : "—") },
                { key: "source", label: "Source", render: (v, r) => r.needsAttention
                    ? <span style={{ fontSize: 12, color: "#993C1D" }}>Reconnect under Add Bank</span>
                    : v === "plaid"
                      ? <span style={{ fontSize: 12, color: "#0F6E56" }}>Connected (live)</span>
                      : <span style={{ fontSize: 12, color: D.textDim }}>On file with Aequum</span> },
                { key: "asOf", label: "Balance as of", render: (v, r) => r.needsAttention ? "—"
                    : v ? fmtDate(v) : (r.lastTransaction ? <span style={{ fontSize: 12, color: D.textDim }}>No balance · last activity {fmtDate(r.lastTransaction)}</span> : "—") },
                { key: "available", label: "Available", align: "right", render: (v) => (v === null || v === undefined ? "—" : fmt(v)) },
                { key: "current", label: "Current", align: "right", render: (v) => (v === null || v === undefined ? "—" : fmt(v)) },
              ]}
            />
            {rows.some((r) => r.source === "stored") && (
              <div style={{ fontSize: 12, color: D.textDim, marginTop: 10 }}>
                “On file with Aequum” accounts are updated from the statements you send us or we pull from read only credentials, so their balance is as of the date shown.
                Connect them under “Add Bank” to see live balances.
              </div>
            )}
          </>
        )}
      </Card>

      <div style={{ height: 20 }} />

      <Card>
        <CardTitle>Transactions</CardTitle>
        {txnLoading ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>Loading…</div>
        ) : txnErr ? (
          <div style={{ fontSize: 13, color: "#993C1D", padding: "8px 4px" }}>
            {txnErr} <button onClick={loadTxns} style={{ marginLeft: 8, background: "none", border: "none", color: B.cyanDark, textDecoration: "underline", cursor: "pointer" }}>Retry</button>
          </div>
        ) : txnRows.length === 0 ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>No transactions yet. History syncs automatically once a bank is linked.</div>
        ) : (
          <DataTable rows={txnRows} exportName="transactions" pageSize={25}
            filterKey="bankAccountLabel"
            columns={[
              { key: "txnDate", label: "Date", render: (v) => fmtDate(v) },
              { key: "bankAccountLabel", label: "Bank Account" },
              { key: "name", label: "Description" },
              { key: "merchantName", label: "Merchant", render: (v) => v || "—" },
              { key: "pfcPrimary", label: "Category", render: (v, r) => v || r.category || "—" },
              { key: "signedAmount", label: "Amount", align: "right", render: (v) => (v === null || v === undefined ? "—" : fmt(v)) },
              { key: "pending", label: "Status", render: (v) => (v ? "Pending" : "Posted") },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

// ─── STATEMENTS ───────────────────────────────────────────────
function Statements() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await api.get("getMyStatements")); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch with the auth header (a plain link can't set x-cp-token), then save
  // the returned blob. The server streams only this account's published files.
  const download = async (s) => {
    setBusyId(s.id);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch("/api/getMyStatementFile?id=" + encodeURIComponent(s.id), { headers: token ? { "x-cp-token": token } : {} });
      if (!res.ok) {
        let m = "Download failed";
        try { const b = await res.json(); if (b && b.error) m = b.error; } catch (_) {}
        throw new Error(m);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = (s.Doc_Type || "xlsx").toLowerCase();
      const dt = s.As_of_Date ? "_" + String(s.As_of_Date).slice(0, 10) : "";
      a.download = (s.Type || "Statement").replace(/[^a-zA-Z0-9]/g, "_") + dt + "." + ext;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) { alert(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: D.text, margin: "0 0 20px" }}>Statements</h1>
      <Card>
        <CardTitle>Published Statements</CardTitle>
        {loading ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>Loading…</div>
        ) : err ? (
          <div style={{ fontSize: 13, color: "#993C1D", padding: "8px 4px" }}>
            {err} <button onClick={load} style={{ marginLeft: 8, background: "none", border: "none", color: B.cyanDark, textDecoration: "underline", cursor: "pointer" }}>Retry</button>
          </div>
        ) : !rows || rows.length === 0 ? (
          <div style={{ fontSize: 13, color: D.textDim, padding: "8px 4px" }}>No statements available yet.</div>
        ) : (
          <DataTable rows={rows}
            columns={[
              { key: "As_of_Date", label: "As of Date", render: (v) => fmtDate(v) },
              { key: "Type", label: "Type" },
              { key: "Doc_Type", label: "Format", render: (v) => (v ? String(v).toUpperCase() : "—") },
              { key: "createdat", label: "Available", render: (v) => fmtDate(v) },
              { key: "id", label: "", render: (v, r) => (
                  <button onClick={() => download(r)} disabled={busyId === r.id}
                    style={{ background: B.cyan, color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 500, cursor: busyId === r.id ? "not-allowed" : "pointer", opacity: busyId === r.id ? 0.6 : 1 }}>
                    {busyId === r.id ? "Downloading…" : "Download"}
                  </button>
                ) },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

// ─── PORTAL SHELL (sidebar + header) ──────────────────────────
function Portal({ user, onLogout }) {
  const [overview, setOverview] = useState(null);
  const [loans, setLoans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nav, setNav] = useState("Home");
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ln] = await Promise.all([api.get("getMyOverview"), api.get("getMyLoans")]);
      setOverview(ov);
      setLoans(ln);
    } catch (e) {
      if (e.message === "UNAUTHORIZED") return onLogout();
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const NAV = [
    { group: "MAIN OPTIONS", items: [
      { key: "Home", label: "Home", Icon: MdHome },
      { key: "Statements", label: "Statements", Icon: MdDescription },
    ] },
    { group: "SYNCED BANKS", items: [
      { key: "Add Bank", label: "Add Bank", Icon: MdAccountBalance },
      { key: "Bank Accounts", label: "Bank Accounts", Icon: MdAccountBalanceWallet },
    ] },
  ];

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: D.bg, fontFamily: "var(--font-sans)", color: D.text, position: "relative" }}>
      {/* Sidebar */}
      <aside style={{ width: collapsed ? 60 : 210, background: D.sidebar, borderRight: "1px solid " + D.navBorder, display: "flex", flexDirection: "column", flexShrink: 0, transition: "width 0.2s ease" }}>
        <div style={{ minHeight: 60, boxSizing: "border-box", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, padding: collapsed ? "20px 6px 14px" : "20px 12px 14px", marginBottom: 10, borderBottom: "0.5px solid rgba(255,255,255,0.1)" }}>
          <img src={mark} alt="" style={{ height: 30, width: "auto", objectFit: "contain", flexShrink: 0 }} />
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, overflow: "hidden" }}>
              <img src={wordmark} alt="Aequum Capital" style={{ height: 15, width: "auto", flexShrink: 0, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
              <span style={{ fontSize: 9.5, fontWeight: 600, color: "var(--brand-cyan-mid, #7cd4eb)", letterSpacing: "0.14em", textTransform: "uppercase", textAlign: "center" }}>Client Portal</span>
            </div>
          )}
        </div>
        <nav style={{ padding: "4px 0", flex: 1 }}>
          {NAV.map((g) => (
            <div key={g.group} style={{ marginBottom: 2 }}>
              {!collapsed && <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "7px 20px", marginTop: 8 }}>{g.group}</div>}
              {g.items.map((it) => {
                const active = nav === it.key;
                return (
                  <button key={it.key} onClick={() => setNav(it.key)} title={collapsed ? it.label : undefined}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = D.sidebarHover; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                    style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 8, padding: collapsed ? "9px 0" : "9px 20px", border: "none", borderLeft: active ? "3px solid " + B.cyan : "3px solid transparent", cursor: "pointer", fontSize: 13,
                      background: active ? "rgba(18,181,219,0.22)" : "transparent", color: active ? "#fff" : "rgba(255,255,255,0.7)", fontWeight: 400 }}>
                    <it.Icon size={18} color={B.cyan} style={{ flexShrink: 0 }} />{!collapsed && it.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* Floating collapse/expand toggle — straddles the sidebar edge */}
      <button onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Expand menu" : "Collapse menu"}
        style={{ position: "absolute", top: 60, left: collapsed ? 60 : 210, transform: "translateX(-50%)", zIndex: 200,
                 display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: "50%",
                 background: D.sidebar, border: "0.5px solid rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.85)",
                 cursor: "pointer", padding: 0, boxShadow: "0 1px 5px rgba(0,0,0,0.35)", transition: "left 0.2s ease" }}>
        {collapsed ? <MdChevronRight size={18} /> : <MdChevronLeft size={18} />}
      </button>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Top menu bar */}
        <header style={{ background: D.headerBar, height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", boxShadow: "0 2px 8px rgba(0,0,0,0.2)", zIndex: 5 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>{nav}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{user.firstName} {user.lastName}</span>
            <button onClick={onLogout} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6, color: "#fff", fontSize: 12, padding: "6px 12px", cursor: "pointer" }}>Sign out</button>
          </div>
        </header>

        <main style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>
          {nav === "Home" && (
            loading ? <div style={{ fontSize: 14, color: D.textDim }}>Loading…</div>
            : error ? (
              <div style={{ background: "#FAECE7", color: "#993C1D", borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
                {error} <button onClick={load} style={{ marginLeft: 8, background: "none", border: "none", color: "#993C1D", textDecoration: "underline", cursor: "pointer" }}>Retry</button>
              </div>
            ) : <Home overview={overview} loans={loans} />
          )}
          {nav === "Statements" && <Statements />}
          {nav === "Add Bank" && <AddBank />}
          {nav === "Bank Accounts" && <BankAccounts />}
        </main>
        <footer style={{ padding: "10px 32px 14px", fontSize: 11, color: D.textDim, borderTop: "1px solid " + D.cardBorder, textAlign: "center" }}>
          {copyrightText()}
        </footer>
      </div>
    </div>
  );
}

// ─── ROOT: auth gate ─────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(USER_KEY);
    if (token && raw) {
      try { setUser(JSON.parse(raw)); } catch (_) {}
    }
    setReady(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("cp_last_activity");
    setUser(null);
  }, []);

  // ── Auto sign-out after 4 hours of inactivity ─────────────────────────────
  // Last-activity time is stored in localStorage (shared across tabs, survives refresh
  // and machine sleep). Re-checked on an interval and whenever the tab regains focus,
  // so a laptop that slept past the limit is signed out the moment it wakes.
  useEffect(() => {
    if (!user) return;
    const KEY = "cp_last_activity";
    const stamp = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch (e) {} };
    let last = 0;
    const onActivity = () => { const t = Date.now(); if (t - last > 30000) { last = t; stamp(); } };
    const check = () => {
      let ts = 0; try { ts = parseInt(localStorage.getItem(KEY) || "0", 10); } catch (e) {}
      if (ts && Date.now() - ts > IDLE_LIMIT_MS) handleLogout();
    };
    stamp(); // seed on login / mount
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    const iv = setInterval(check, 60000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      clearInterval(iv);
    };
  }, [user, handleLogout]);

  if (!ready) return null;
  if (!user) return <LoginPage onLogin={setUser} />;
  return <Portal user={user} onLogout={handleLogout} />;
}
